import { SESSION_INACTIVITY_HOURS } from "@redbelly-tokeniser/mystake-common-sdk";
import { useEffect } from "react";
import { EVENTS, useAnalytics } from "../../../analytics";
import { sessionService } from "../../../util";
import { readRememberMePreference } from "../../signInFlow/signin/rememberMeStorage";

const sessionValidationEnabled = import.meta.env.VITE_SESSION_VALIDATION_ENABLED === "true";

const inactivityThresholdMs = (rememberMe: boolean): number =>
  (rememberMe ? SESSION_INACTIVITY_HOURS.rememberMe : SESSION_INACTIVITY_HOURS.standard) * 60 * 60 * 1000;

// Custom hook to track user activity
export const useActivityTracker = (): void => {
  useEffect(() => {
    const events: string[] = ["click", "keypress", "scroll", "mousemove"];
    const updateActivity = (): void => {
      const now = new Date().toISOString();
      localStorage.setItem("lastActivity", now);
    };
    if (sessionValidationEnabled) {
      updateActivity();
      events.forEach((event) => {
        window.addEventListener(event, updateActivity);
      });
    }

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);
};

// Custom hook to save last activity when the tab or browser is closed
export const useSaveOnClose = (): void => {
  useEffect(() => {
    const handleTabClose = async (): Promise<void> => {
      const hasSession = sessionService.getCurrentSessionId();
      if (!hasSession) {
        return;
      }
      const now = new Date().toISOString();
      localStorage.setItem("lastActivity", now);
      await sessionService.updateLastAccess(new Date(now));
      localStorage.setItem("lastSynced", now);
    };
    if (sessionValidationEnabled) {
      window.addEventListener("beforeunload", handleTabClose);
      window.addEventListener("visibilitychange", handleTabClose);
    }
    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
      window.removeEventListener("visibilitychange", handleTabClose);
    };
  }, []);
};

// Custom hook to check for user inactivity
export const useInactivityTimeout = (): void => {
  const { track } = useAnalytics();

  useEffect(() => {
    const checkActivity = async (): Promise<void> => {
      const hasSession = sessionService.getCurrentSessionId();
      if (!sessionValidationEnabled || !hasSession) {
        return;
      }
      const rememberMe = readRememberMePreference();
      const lastActivity = localStorage.getItem("lastActivity");
      const lastSynced = localStorage.getItem("lastSynced");

      const now = new Date();
      // 15 mins sync gap in ms
      const syncGap = 15 * 60 * 1000;

      const lastSyncedDate = lastSynced ? new Date(lastSynced) : new Date();
      const lastActivityDate = lastActivity ? new Date(lastActivity) : new Date();

      if (now.getTime() - lastSyncedDate.getTime() > syncGap) {
        sessionService.updateLastAccess(lastActivityDate);
        localStorage.setItem("lastSynced", now.toISOString());
      }

      if (now.getTime() - lastActivityDate.getTime() > inactivityThresholdMs(rememberMe)) {
        track(EVENTS.SESSION_EXPIRED);
        // The reason separates an idle session from an explicit sign-out server-side.
        await sessionService.revokeSession("expired");
        track(EVENTS.SIGN_OUT_COMPLETED, { trigger: "session_expired" });
      }
    };

    const interval = setInterval(checkActivity, 10 * 60 * 1000); // check every 10 minutes
    return () => clearInterval(interval);
  }, [track]);
};
